import type { Style } from "@bezpart-board/shared";
import type { SceneObject } from "./SceneHost";
import { generateWavyPoints } from "./geometry/wavyPath";
import { lineAxisSnapEnabled } from "./lineSnapPrefs";
import {
  buildPathD,
  clonePoints,
  computePathBBox,
  type PathPoint,
} from "./pathGeometry";
import { pathStrokeDash, pathStrokeWidth } from "../lib/board/sceneStyle";

export function isSegmentObject(
  obj: SceneObject
): obj is SceneObject & { type: "line" | "arrow"; points: PathPoint[] } {
  return (
    (obj.type === "line" || obj.type === "arrow") &&
    Array.isArray(obj.points) &&
    obj.points.length >= 2
  );
}

export function segmentEndpoints(obj: SceneObject): [PathPoint, PathPoint] | null {
  if (!isSegmentObject(obj)) return null;
  const a = obj.points[0]!;
  const b = obj.points[obj.points.length - 1]!;
  return [
    [a[0], a[1]],
    [b[0], b[1]],
  ];
}

export function getSegmentDisplayPoints(obj: SceneObject): PathPoint[] {
  const ends = segmentEndpoints(obj);
  if (!ends) return [];
  const [a, b] = ends;
  const strokeWidth = pathStrokeWidth(obj);
  if (pathStrokeDash(obj) === "wavy") {
    return generateWavyPoints(a, b, strokeWidth);
  }
  return [a, b];
}

export function computeSegmentBBox(obj: SceneObject): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const pts = getSegmentDisplayPoints(obj);
  if (pts.length < 2) return { x: obj.x, y: obj.y, w: obj.w ?? 1, h: obj.h ?? 1 };
  return computePathBBox(pts, pathStrokeWidth(obj));
}

/**
 * Shift: в узком конусе (~5°) линия «срывается» на горизонталь или вертикаль;
 * между этими зонами угол полностью свободный.
 */
const HV_SNAP_TOL_DEG = 5;

function normalizeAngleDeg(deg: number): number {
  let a = deg;
  while (a <= -180) a += 360;
  while (a > 180) a -= 360;
  return a;
}

function angularDistanceDeg(a: number, b: number): number {
  let d = Math.abs(a - b);
  if (d > 180) d = 360 - d;
  return d;
}

function distToHorizontalDeg(angleDeg: number): number {
  return Math.min(
    angularDistanceDeg(angleDeg, 0),
    angularDistanceDeg(angleDeg, 180),
    angularDistanceDeg(angleDeg, -180)
  );
}

function distToVerticalDeg(angleDeg: number): number {
  return Math.min(
    angularDistanceDeg(angleDeg, 90),
    angularDistanceDeg(angleDeg, -90)
  );
}

/** Snap end: к горизонтали/вертикали в конусе ~5°, иначе без округления. */
export function snapSegmentEnd(start: PathPoint, end: PathPoint, shiftKey: boolean): PathPoint {
  if (!shiftKey) return end;
  // Прилипание выключено в настройках — отрезок ровно под тем углом,
  // под которым его провели.
  if (!lineAxisSnapEnabled()) return end;
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return end;

  const angleDeg = normalizeAngleDeg((Math.atan2(dy, dx) * 180) / Math.PI);
  const distHoriz = distToHorizontalDeg(angleDeg);
  const distVert = distToVerticalDeg(angleDeg);

  if (distHoriz <= HV_SNAP_TOL_DEG && distHoriz <= distVert) {
    return [
      Math.round(start[0] + (dx >= 0 ? len : -len)),
      Math.round(start[1]),
    ];
  }
  if (distVert <= HV_SNAP_TOL_DEG) {
    return [
      Math.round(start[0]),
      Math.round(start[1] + (dy >= 0 ? len : -len)),
    ];
  }

  return end;
}

export function segmentLength(a: PathPoint, b: PathPoint): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

export function applySegmentStyleFields(style: {
  stroke: string;
  strokeWidth: number;
  strokeLinecap: Style["strokeLinecap"];
  strokeDash?: Style["strokeDash"];
}): Style {
  return {
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    strokeLinecap: style.strokeLinecap ?? "round",
    strokeLinejoin: "round",
    strokeDash: style.strokeDash ?? "solid",
  };
}

const SVG_NS = "http://www.w3.org/2000/svg";

export function createSegmentPreviewSvg(): {
  root: SVGSVGElement;
  path: SVGPathElement;
  setSegment: (
    a: PathPoint,
    b: PathPoint,
    stroke: string,
    strokeWidth: number,
    linecap: string,
    strokeDash?: Style["strokeDash"]
  ) => void;
} {
  const root = document.createElementNS(SVG_NS, "svg");
  root.setAttribute("class", "wb-segment-preview");
  root.style.position = "absolute";
  root.style.left = "0";
  root.style.top = "0";
  root.style.width = "100%";
  root.style.height = "100%";
  root.style.overflow = "visible";
  root.style.pointerEvents = "none";

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "none");
  root.appendChild(path);

  return {
    root,
    path,
    setSegment(a, b, stroke, strokeWidth, linecap, strokeDash = "solid") {
      const display =
        strokeDash === "wavy" ? generateWavyPoints(a, b, strokeWidth) : [a, b];
      if (display.length < 2) {
        path.setAttribute("d", "");
        return;
      }
      const bbox = computePathBBox(display, strokeWidth);
      root.style.left = `${bbox.x}px`;
      root.style.top = `${bbox.y}px`;
      root.style.width = `${bbox.w}px`;
      root.style.height = `${bbox.h}px`;
      root.setAttribute("viewBox", `0 0 ${bbox.w} ${bbox.h}`);
      path.setAttribute("d", buildPathD(display, bbox.x, bbox.y));
      path.setAttribute("stroke", stroke);
      path.setAttribute("stroke-width", String(strokeWidth));
      path.setAttribute("stroke-linecap", linecap);
      path.setAttribute("stroke-linejoin", "round");
      if (strokeDash === "dashed") {
        path.setAttribute("stroke-dasharray", `${strokeWidth * 4} ${strokeWidth * 2.5}`);
      } else if (strokeDash === "dotted") {
        path.setAttribute("stroke-dasharray", `0 ${strokeWidth * 2}`);
      } else {
        path.removeAttribute("stroke-dasharray");
      }
    },
  };
}

export { clonePoints };
